import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useEditor, EditorContent, ReactRenderer } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { Box, Typography } from "@mui/material";
import MentionList from "../wiki/MentionList";
import { MentionItem } from "../../services/wiki.service";
import {
  fetchFileNoteTemplatesForMentions,
  fetchSkillsForMentions,
  FileNoteTemplateMentionCandidate,
  SkillMentionCandidate,
} from "../../services/docTemplates.service";
import { fetchAdvisorRulesForMentions, AdvisorRuleMentionCandidate } from "../../services/advisorRules.service";
import { AI_SKILL_CHANGED_EVENT } from "../../hooks/useSkillChangeListener";
import { CLIENT_CHANGED_EVENT } from "../../hooks/useClientChangeListener";
import { sortByCRMPref, useCRMSort } from "../../hooks/useCRMSort";
import {
  fetchClientsForMentions,
  ClientMentionCandidate,
} from "../../services/clients.service";
import {
  fetchFileMentions,
  FileMentionCandidate,
} from "../../services/agentMentions.service";
import { fetchWorkflows } from "../../services/workflow.service";
import { triggerNavigation } from "../../hooks/useNavigationHandler";

export interface ChatMessageInputHandle {
  getText: () => string;
  getMentionedClients: () => { id: string; name: string }[];
  getMentionedFiles: () => { id: string; name: string; type: "shared-file" | "private-file" }[];
  getMentionedTemplates: () => { id: string; name: string }[];
  getMentionedSkills: () => { id: string; name: string }[];
  getMentionedRules: () => { id: string; name: string }[];
  setText: (text: string) => void;
  clear: () => void;
  focus: () => void;
}

interface Props {
  placeholder?: string;
  disabled?: boolean;
  onSend: () => void;
  onEmptyChange?: (isEmpty: boolean) => void;
}

interface ClientMentionItem extends MentionItem {
  /** Plain client name, with no email — used for the inserted chip/plain-text so the
   *  input doesn't stay cluttered with the email after selection. The dropdown itself
   *  still shows `label` (name + email) to help tell same-named clients apart. */
  name: string;
  mentionType?: "client";
}

interface TemplateMentionItem extends MentionItem {
  name: string;
}

interface RuleMentionItem extends MentionItem {
  name: string;
}

interface FileMentionItem extends MentionItem {
  name: string;
  fileType: "shared-file" | "private-file";
}

interface RuleCommandItem extends MentionItem {
  /** Literal text inserted into the editor when this command is picked (includes trailing space). */
  insertText: string;
  /** Trigger char auto-inserted right after insertText, chaining straight into that picker. */
  chainChar?: "/" | "~" | "^" | "#";
}

/** Top-level workflows exposed by the slash picker. */
const RULE_COMMANDS: RuleCommandItem[] = [
  { id: "workflow", label: "/workflow", type: "build or open a workflow", insertText: "/workflow ", chainChar: "/" },
  { id: "template", label: "/template", type: "templates and rules", insertText: "/template ", chainChar: "/" },
  { id: "skill", label: "/skill", type: "build and manage skills", insertText: "/skill ", chainChar: "/" },
  { id: "use-skill", label: "/use skill", type: "choose a skill", insertText: "/use skill ", chainChar: "~" },
];

const WORKFLOW_COMMANDS: RuleCommandItem[] = [
  { id: "back-to-main", label: "Back to commands", type: "navigation", insertText: "" },
  { id: "create-workflow", label: "Create workflow", type: "workflow", insertText: "create " },
  { id: "trigger-workflow", label: "Open workflows", type: "workflow", insertText: "trigger ", chainChar: "~" },
];

const TEMPLATE_COMMANDS: RuleCommandItem[] = [
  { id: "back-to-main", label: "Back to commands", type: "navigation", insertText: "" },
  { id: "show-templates", label: "Show templates", type: "template", insertText: "show" },
  { id: "add-template", label: "Add template", type: "template", insertText: "add " },
  { id: "edit-template", label: "Edit template", type: "template", insertText: "edit ", chainChar: "~" },
  { id: "remove-template", label: "Remove template", type: "template", insertText: "remove ", chainChar: "~" },
  { id: "show-template-rules", label: "Show rules", type: "template rule", insertText: "show rules ", chainChar: "~" },
  { id: "add-template-rule", label: "Add rule", type: "template rule", insertText: "add rule ", chainChar: "~" },
  { id: "edit-template-rule", label: "Edit rule", type: "template rule", insertText: "edit rule ", chainChar: "~" },
  { id: "remove-template-rule", label: "Remove rule", type: "template rule", insertText: "remove rule ", chainChar: "~" },
];

const SKILL_COMMANDS: RuleCommandItem[] = [
  { id: "back-to-main", label: "Back to commands", type: "navigation", insertText: "" },
  { id: "show-skills", label: "Show skills", type: "skill", insertText: "show" },
  { id: "add-skill", label: "Add skill", type: "skill", insertText: "add " },
  { id: "upload-skill", label: "Upload skill", type: "skill", insertText: "upload " },
  { id: "edit-skill", label: "Edit skill", type: "skill", insertText: "edit ", chainChar: "~" },
  { id: "delete-skill", label: "Delete skill", type: "skill", insertText: "delete ", chainChar: "~" },
  { id: "use-managed-skill", label: "Use skill", type: "skill", insertText: "use ", chainChar: "~" },
];

/** Shared renderLabel/renderText body for every Mention config below — only the optional
 *  display prefix (e.g. "@", "~") differs; rule commands/removable-rules use no prefix. */
const mentionText = (prefix = "") => ({ node }: any) => `${prefix}${node.attrs.label ?? node.attrs.id}`;

/** Shared command() body for inserting a mention chip + trailing separator — the only things
 *  that differ per picker are the node type and what separator follows the chip. */
function insertMentionChip(ed: any, range: any, nodeType: string, props: any, suffix: string) {
  ed.chain().focus().deleteRange(range)
    .insertContent({ type: nodeType, attrs: { id: props.id, label: props.name ?? props.label } })
    .insertContent(suffix)
    .run();
}

function extractMentionNodes(doc: any, nodeType: string): { id: string; name: string }[] {
  const found: { id: string; name: string }[] = [];
  const walk = (node: any) => {
    if (!node) return;
    if (node.type === nodeType && node.attrs?.id) {
      found.push({ id: node.attrs.id, name: node.attrs.label ?? node.attrs.id });
    }
    if (Array.isArray(node.content)) node.content.forEach(walk);
  };
  walk(doc);
  const seen = new Set<string>();
  return found.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)));
}

// Additional named instances of the same Mention node so client (@), rule-command (/),
// template (~), rule-to-remove (^), and contextual file (#) suggestions can
// coexist with distinct node types / trigger chars.
const RuleCommandMention = Mention.extend({ name: "ruleCommand" });
const TemplateMention = Mention.extend({ name: "templateMention" });
const RuleMention = Mention.extend({ name: "ruleMention" });
const FileMention = Mention.extend({ name: "fileMention" });

interface AutoCommandTriggerRange {
  from: number;
  to: number;
}

const autoCommandTriggerKey = new PluginKey<AutoCommandTriggerRange | null>("chatAutoCommandTrigger");

const AutoCommandTrigger = Extension.create({
  name: "autoCommandTrigger",
  addProseMirrorPlugins() {
    return [
      new Plugin<AutoCommandTriggerRange | null>({
        key: autoCommandTriggerKey,
        state: {
          init: () => null,
          apply(transaction, current) {
            const action = transaction.getMeta(autoCommandTriggerKey) as
              | { type: "set"; range: AutoCommandTriggerRange }
              | { type: "clear" }
              | undefined;
            if (action?.type === "set") return action.range;
            if (action?.type === "clear" || !current) return null;
            const from = transaction.mapping.map(current.from);
            const to = transaction.mapping.map(current.to);
            return transaction.doc.textBetween(from, to) === "/" ? { from, to } : null;
          },
        },
        props: {
          decorations(state) {
            const range = autoCommandTriggerKey.getState(state);
            return range
              ? DecorationSet.create(state.doc, [
                  Decoration.inline(range.from, range.to, { class: "chat-auto-command-trigger" }),
                ])
              : null;
          },
        },
      }),
    ];
  },
});

const markAutoCommandTrigger = (editor: any): void => {
  const to = editor.state.selection.from;
  const from = to - 1;
  if (from >= 0 && editor.state.doc.textBetween(from, to) === "/") {
    editor.view.dispatch(
      editor.state.tr.setMeta(autoCommandTriggerKey, { type: "set", range: { from, to } }),
    );
  }
};

const clearAutoCommandTrigger = (editor: any): void => {
  const range = autoCommandTriggerKey.getState(editor.state);
  if (!range) return;
  editor.view.dispatch(
    editor.state.tr
      .delete(range.from, range.to)
      .setMeta(autoCommandTriggerKey, { type: "clear" }),
  );
};

/**
 * Chat input with @ / / / ~ / ^ pickers — clients, rule commands, file-note templates,
 * and rules-to-remove, respectively. Modeled on CommentEditor.tsx (Tiptap + Mention +
 * MentionList), keeping this widget's plain-Enter-to-send / Shift+Enter-newline UX
 * (see activeSuggestionsRef below).
 */
const ChatMessageInput = forwardRef<ChatMessageInputHandle, Props>(
  ({ placeholder = "Ask Prospo AI...", disabled = false, onSend, onEmptyChange }, ref) => {
    const [clientSortPref] = useCRMSort();
    // Org client / file-note-template lists — fetched once per mount (small, org-scoped
    // datasets), filtered in-memory per keystroke rather than re-fetched. Scoped to this
    // component instance so neither can outlive a logout/login as a different org.
    const templateCacheRef = useRef<FileNoteTemplateMentionCandidate[] | null>(null);
    const skillCacheRef = useRef<SkillMentionCandidate[] | null>(null);
    const clientCacheRef = useRef<ClientMentionCandidate[] | null>(null);
    const autoHierarchyTriggerRef = useRef(false);
    const previousEditorTextRef = useRef("");

    useEffect(() => {
      const invalidateClients = () => { clientCacheRef.current = null; };
      window.addEventListener(CLIENT_CHANGED_EVENT, invalidateClients);
      return () => window.removeEventListener(CLIENT_CHANGED_EVENT, invalidateClients);
    }, []);

    useEffect(() => {
      const invalidateSkills = () => { skillCacheRef.current = null; };
      const invalidateClients = () => { clientCacheRef.current = null; };
      window.addEventListener(AI_SKILL_CHANGED_EVENT, invalidateSkills);
      window.addEventListener(CLIENT_CHANGED_EVENT, invalidateClients);
      return () => {
        window.removeEventListener(AI_SKILL_CHANGED_EVENT, invalidateSkills);
        window.removeEventListener(CLIENT_CHANGED_EVENT, invalidateClients);
      };
    }, []);
    // Rules are actively mutated within the same session (add/remove), so unlike clients/
    // templates this is deliberately NOT cached — always fetched fresh per keystroke.

    // Non-empty while ANY suggestion dropdown is open — lets handleKeyDown
    // below yield Enter/ArrowUp/ArrowDown to whichever Suggestion plugin is active instead
    // of guessing ProseMirror's plugin keydown priority order. A Set (keyed per picker)
    // rather than one shared boolean so one picker's onExit can never clobber a picker
    // that opened immediately afterwards in the same transaction (e.g. / chaining into ~).
    const activeSuggestionsRef = useRef<Set<string>>(new Set());

    // Set before inserting a template chip for an edit/remove rule flow so the
    // template picker can chain directly into the rule picker.
    const pendingTemplateFollowupRef = useRef<"selectRule" | null>(null);
    const pendingRuleEditRef = useRef(false);
    const pendingSkillPickerRef = useRef(false);
    const pendingSkillActionRef = useRef<"use" | "edit" | "delete" | null>(null);
    const pendingFilesClientIdRef = useRef<string | null>(null);
    // Set when "Open workflows" is picked so the ~ picker lists workflows
    // instead of templates/skills — picking one navigates straight to it,
    // no chat round-trip needed (same reasoning as /workflow trigger's bare
    // no-name shortcut in ChatInterface.tsx).
    const pendingWorkflowPickerRef = useRef(false);

    const loadSkills = async (): Promise<SkillMentionCandidate[]> => {
      if (!skillCacheRef.current) {
        try {
          skillCacheRef.current = await fetchSkillsForMentions();
        } catch {
          skillCacheRef.current = [];
        }
      }
      return skillCacheRef.current;
    };

    // Drives a plain React-rendered placeholder overlay (no Tiptap Placeholder
    // extension in this app's dependency set, and no guarantee the CSS-only
    // ::before-on-.is-empty trick applies without it).
    const [isEmpty, setIsEmpty] = useState(true);

    const searchClientMentions = async (query: string): Promise<ClientMentionItem[]> => {
      // Refresh whenever a new bare @ picker opens, then filter locally while typing.
      if (!clientCacheRef.current || !query.trim()) {
        try {
          clientCacheRef.current = await fetchClientsForMentions();
        } catch {
          clientCacheRef.current = [];
        }
      }
      const q = query.trim().toLowerCase();
      const matchingClients = q
        ? clientCacheRef.current.filter((client) => client.name.toLowerCase().includes(q))
        : clientCacheRef.current;
      const clients = sortByCRMPref(matchingClients, clientSortPref);
      const clientItems: ClientMentionItem[] = clients.map((client) => ({
        id: client.id,
        label: client.name,
        name: client.name,
        type: "client",
        mentionType: "client",
      }));
      return clientItems;
    };

    const latestClientIdBeforeCursor = (editor: any): string | null => {
      let clientId: string | null = null;
      editor.state.doc.nodesBetween(0, editor.state.selection.from, (node: any) => {
        if (node.type?.name === "mention" && node.attrs?.id) clientId = String(node.attrs.id);
      });
      return clientId;
    };

    const searchRuleCommands = async (query: string, editor: any): Promise<RuleCommandItem[]> => {
      const q = query.trim().toLowerCase();
      // Derive the menu from document content, never mutable picker state. This
      // prevents a cancelled /template flow from making the next fresh `/` open
      // the template submenu.
      const textBeforeCursor = editor.state.doc.textBetween(0, editor.state.selection.from, " ", " ");
      const isTemplateSubmenu = /(?:^|\s)\/template\s+\/[^/\s]*$/i.test(textBeforeCursor);
      const isSkillSubmenu = /(?:^|\s)\/skill\s+\/[^/\s]*$/i.test(textBeforeCursor);
      const isWorkflowSubmenu = /(?:^|\s)\/workflow\s+\/[^/\s]*$/i.test(textBeforeCursor);
      const contextualCommands = latestClientIdBeforeCursor(editor)
        ? [
            ...RULE_COMMANDS,
            { id: "files", label: "/files", type: "files for selected client", insertText: "/files ", chainChar: "#" as const },
          ]
        : RULE_COMMANDS;
      const items = isTemplateSubmenu
        ? TEMPLATE_COMMANDS
        : isSkillSubmenu
          ? SKILL_COMMANDS
          : isWorkflowSubmenu
            ? WORKFLOW_COMMANDS
            : contextualCommands;
      return q ? items.filter((item) => item.label.toLowerCase().includes(q)) : items;
    };

    const searchFileMentions = async (query: string): Promise<FileMentionItem[]> => {
      const clientId = pendingFilesClientIdRef.current;
      if (!clientId) return [];
      let files: FileMentionCandidate[];
      try {
        files = await fetchFileMentions(query, clientId);
      } catch {
        files = [];
      }
      return files.map((file) => ({
        id: `${file.type}:${file.id}`,
        label: file.name,
        name: file.name,
        type: file.type === "shared-file" ? "shared file" : "private file",
        fileType: file.type,
      }));
    };

    const searchTemplateMentions = async (query: string): Promise<TemplateMentionItem[]> => {
      if (pendingWorkflowPickerRef.current) {
        // Deliberately not cached — a workflow created moments earlier in this
        // same session (via /workflow create) must show up immediately.
        let workflows: { id: string; name: string }[];
        try {
          const res: any = await fetchWorkflows();
          const list = Array.isArray(res) ? res : res?.data || [];
          workflows = list.map((w: any) => ({ id: w._id, name: w.name }));
        } catch {
          workflows = [];
        }
        const q = query.trim().toLowerCase();
        const matches = q ? workflows.filter((w) => w.name.toLowerCase().includes(q)) : workflows;
        return matches.map((w) => ({ id: `workflow:${w.id}`, label: w.name, name: w.name, type: "workflow" }));
      }
      if (pendingSkillPickerRef.current) {
        const q = query.trim().toLowerCase();
        const skills = await loadSkills();
        const matches = q ? skills.filter((skill) => skill.name.toLowerCase().includes(q)) : skills;
        return matches.map((skill) => ({
          id: `skill:${skill.id}`,
          label: skill.name,
          name: skill.name,
          type: "skill",
        }));
      }
      if (!templateCacheRef.current) {
        try {
          templateCacheRef.current = await fetchFileNoteTemplatesForMentions();
        } catch {
          templateCacheRef.current = [];
        }
      }
      const q = query.trim().toLowerCase();
      const matches = q
        ? templateCacheRef.current.filter((t) => t.name.toLowerCase().includes(q))
        : templateCacheRef.current;
      return matches.map((t) => ({ id: t.id, label: t.name, name: t.name, type: "template" }));
    };

    // Scope is inferred from the selected ~template chip before the cursor.
    const searchRemovableRules = async (editor: any, query: string): Promise<RuleMentionItem[]> => {
      let scopeTemplateId: string | undefined;
      const cursorPos = editor.state.selection.from;
      editor.state.doc.nodesBetween(0, cursorPos, (node: any) => {
        if (node.type?.name === "templateMention") scopeTemplateId = node.attrs.id;
      });
      let rules: AdvisorRuleMentionCandidate[];
      try {
        rules = await fetchAdvisorRulesForMentions(scopeTemplateId);
      } catch {
        rules = [];
      }
      const q = query.trim().toLowerCase();
      const matches = q ? rules.filter((r) => r.ruleText.toLowerCase().includes(q)) : rules;
      return matches.map((r) => ({
        id: r.id,
        label: r.ruleText,
        name: r.ruleText,
        type: scopeTemplateId ? "template rule" : "global rule",
      }));
    };

    // Shared render() factory for all three pickers — only the activeSuggestions `key`
    // and the anchor/positioning logic differ per picker; behaviour is otherwise identical.
    const makeSuggestionRender = (key: string) => () => {
      let renderer: ReactRenderer;
      let container: HTMLDivElement;
      let handleOutsidePointerDown: ((event: PointerEvent) => void) | undefined;

      // Anchored to the whole input box's rect (not the caret's exact pixel
      // position) so the dropdown always sits at a fixed, predictable spot.
      // Prefers opening above the input (the usual case, since the input sits
      // at the bottom of the chat panel) but flips to below when there isn't
      // enough room above — e.g. a short chat window or an input pinned near
      // the top — so the list is never clipped off-screen.
      const DROPDOWN_MAX_HEIGHT = 420;
      const position = (editorEl: HTMLElement) => {
        const box = editorEl.getBoundingClientRect();
        const margin = 10;
        const width = Math.max(box.width, 300);
        const left = Math.min(box.left, window.innerWidth - width - margin);
        const spaceAbove = box.top;
        const openBelow = spaceAbove < DROPDOWN_MAX_HEIGHT + margin;
        Object.assign(container.style, {
          position: "fixed",
          zIndex: "9999",
          left: `${Math.max(left, margin)}px`,
          width: `${width}px`,
          maxHeight: `${DROPDOWN_MAX_HEIGHT}px`,
          top: openBelow ? `${box.bottom + margin}px` : "",
          bottom: openBelow ? "" : `${window.innerHeight - box.top + margin}px`,
        });
      };

      return {
        onStart(props: any) {
          activeSuggestionsRef.current.add(key);
          container = document.createElement("div");
          document.body.appendChild(container);
          renderer = new ReactRenderer(MentionList, { props, editor: props.editor });
          container.appendChild(renderer.element);
          position(props.editor.view.dom);
          handleOutsidePointerDown = (event: PointerEvent) => {
            const target = event.target as Node | null;
            if (
              key === "command"
              && target
              && !container.contains(target)
            ) {
              clearAutoCommandTrigger(props.editor);
            }
          };
          document.addEventListener("pointerdown", handleOutsidePointerDown);
        },
        onUpdate(props: any) {
          renderer.updateProps(props);
          position(props.editor.view.dom);
        },
        onKeyDown(props: any) {
          if (props.event.key === "Escape") {
            activeSuggestionsRef.current.delete(key);
            if (key === "command") clearAutoCommandTrigger(props.editor);
          }
          return (renderer.ref as any)?.onKeyDown(props) ?? false;
        },
        onExit() {
          activeSuggestionsRef.current.delete(key);
          if (key === "template") pendingSkillPickerRef.current = false;
          if (key === "file") pendingFilesClientIdRef.current = null;
          if (handleOutsidePointerDown) {
            document.removeEventListener("pointerdown", handleOutsidePointerDown);
          }
          renderer.destroy();
          container.remove();
        },
      };
    };

    const editor = useEditor({
      extensions: [
        StarterKit,
        AutoCommandTrigger,
        Mention.configure({
          HTMLAttributes: { class: "chat-mention" },
          renderLabel: mentionText("@"),
          renderText: mentionText("@"),
          suggestion: {
            items: ({ query }) => searchClientMentions(query),
            render: makeSuggestionRender("client"),
            // Inserts the plain name (not the "name (email)" dropdown label) — the
            // email was only there to help pick the right same-named client.
            command: ({ editor: ed, range, props }) => {
              insertMentionChip(ed, range, "mention", props, " /");
              window.setTimeout(() => {
                if (!ed.isDestroyed) markAutoCommandTrigger(ed);
              }, 0);
            },
          },
        }),
        RuleCommandMention.configure({
          HTMLAttributes: { class: "chat-rule-command" },
          renderLabel: mentionText(),
          renderText: mentionText(),
          suggestion: {
            char: "/",
            items: ({ query, editor: ed }) => searchRuleCommands(query, ed),
            render: makeSuggestionRender("command"),
            command: ({ editor: ed, range, props }: any) => {
              if (props.id === "back-to-main") {
                const selectionFrom = ed.state.selection.from;
                const textBeforeCursor = ed.state.doc.textBetween(0, selectionFrom, " ", " ");
                const activeHierarchyCommand = textBeforeCursor.match(/(?:^|\s)\/(?:template|skill|workflow)\s+\/[^/\s]*$/i)?.[0];
                if (activeHierarchyCommand) {
                  const leadingWhitespace = activeHierarchyCommand.match(/^\s/)?.[0] ?? "";
                  ed.chain()
                    .focus()
                    .deleteRange({
                      from: selectionFrom - activeHierarchyCommand.length,
                      to: selectionFrom,
                    })
                    .insertContent(`${leadingWhitespace}/`)
                    .run();
                }
                return;
              }

              // Plain text, not a mention node/chip — the command is followed by
              // free-text arguments the advisor keeps typing inline. Commands with a
              // chainChar auto-open the next picker by inserting its trigger char
              // right after the command text.
              pendingTemplateFollowupRef.current = props.id === "remove-template-rule" || props.id === "edit-template-rule" ? "selectRule" : null;
              pendingRuleEditRef.current = props.id === "edit-template-rule";
              const skillAction = props.id === "use-skill" || props.id === "use-managed-skill"
                ? "use"
                : props.id === "edit-skill"
                  ? "edit"
                  : props.id === "delete-skill"
                    ? "delete"
                    : null;
              pendingSkillActionRef.current = skillAction;
              pendingSkillPickerRef.current = skillAction !== null;
              pendingWorkflowPickerRef.current = props.id === "trigger-workflow";
              if (props.id === "files") {
                pendingFilesClientIdRef.current = latestClientIdBeforeCursor(ed);
              }
              // Use the exact same chained-trigger transaction as /delete-template ~.
              // This lets Tiptap close the slash picker and open the follow-up picker
              // atomically instead of leaving a literal @ with no dropdown.
              const text = props.chainChar ? `${props.insertText}${props.chainChar}` : props.insertText;
              ed.chain().focus().deleteRange(range).insertContent(text).run();
            },
          },
        }),
        TemplateMention.configure({
          HTMLAttributes: { class: "chat-template-mention" },
          renderLabel: mentionText("~"),
          renderText: mentionText("~"),
          suggestion: {
            char: "~",
            items: ({ query }) => searchTemplateMentions(query),
            render: makeSuggestionRender("template"),
            command: ({ editor: ed, range, props }) => {
              if (pendingWorkflowPickerRef.current) {
                pendingWorkflowPickerRef.current = false;
                // Terminal action, not a chip to keep composing around — clear the
                // whole "/workflow trigger ~..." command and go straight there, the
                // same no-LLM-round-trip navigation as the bare command.
                ed.chain().focus().clearContent().run();
                triggerNavigation({
                  actionType: "navigate",
                  route: `/admin?tab=workflows&workflowId=${props.id.slice("workflow:".length)}`,
                });
                return;
              }
              // /remove-template-rule chains straight into the ^ rule picker (no free
              // text follows); /add-template-rule / /show-template-rules / /delete-template
              // take "<template>, <rest>" so a plain ", " follows instead.
              const followup = pendingTemplateFollowupRef.current;
              pendingTemplateFollowupRef.current = null;
              const skillAction = pendingSkillActionRef.current;
              pendingSkillActionRef.current = null;
              pendingSkillPickerRef.current = false;
              const suffix = skillAction === "delete" ? " " : followup === "selectRule" ? " ^" : ", ";
              insertMentionChip(ed, range, "templateMention", props, suffix);
            },
          },
        }),
        RuleMention.configure({
          HTMLAttributes: { class: "chat-rule-mention" },
          renderLabel: mentionText(),
          renderText: mentionText(),
          suggestion: {
            char: "^",
            items: ({ query, editor: ed }) => searchRemovableRules(ed, query),
            render: makeSuggestionRender("rule"),
            command: ({ editor: ed, range, props }) => {
              const suffix = pendingRuleEditRef.current ? ", " : " ";
              pendingRuleEditRef.current = false;
              insertMentionChip(ed, range, "ruleMention", props, suffix);
            },
          },
        }),
        FileMention.configure({
          HTMLAttributes: { class: "chat-file-mention" },
          renderLabel: mentionText(),
          renderText: mentionText(),
          suggestion: {
            char: "#",
            allow: () => Boolean(pendingFilesClientIdRef.current),
            items: ({ query }) => searchFileMentions(query),
            render: makeSuggestionRender("file"),
            command: ({ editor: ed, range, props }) => {
              insertMentionChip(ed, range, "fileMention", props, " ");
              pendingFilesClientIdRef.current = null;
            },
          },
        }),
      ],
      editable: !disabled,
      autofocus: false,
      editorProps: {
        handleTextInput(view, _from, _to, text) {
          const range = autoCommandTriggerKey.getState(view.state);
          if (!range) return false;
          view.dispatch(
            view.state.tr
              .delete(range.from, range.to)
              .insertText(text, range.from)
              .setMeta(autoCommandTriggerKey, { type: "clear" }),
          );
          return true;
        },
        handleKeyDown(_view, event) {
          if (activeSuggestionsRef.current.size > 0) return false; // let the active Suggestion plugin handle it
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            if (!disabled) onSend();
            return true;
          }
          return false;
        },
      },
      onUpdate: ({ editor: ed }) => {
        setIsEmpty(ed.isEmpty);
        onEmptyChange?.(ed.isEmpty);

        // Directly typed hierarchy roots behave the same as picking them from the menu.
        const text = ed.getText();
        const textWasAdded = text.length > previousEditorTextRef.current.length;
        previousEditorTextRef.current = text;
        const typedTemplate = /(?:^|\s)\/template$/i.test(text);
        const typedSkill = /(?:^|\s)\/skill$/i.test(text);
        const typedUseSkill = /(?:^|\s)\/use skill$/i.test(text);
        const typedFiles = /(?:^|\s)\/files$/i.test(text) && Boolean(latestClientIdBeforeCursor(ed));
        // Do not auto-open after Backspace reduces `/template /` to `/template`.
        // Without this direction check, the trigger is immediately reinserted and
        // the user becomes trapped in the submenu.
        if (textWasAdded && !autoHierarchyTriggerRef.current && (typedTemplate || typedSkill || typedUseSkill || typedFiles)) {
          autoHierarchyTriggerRef.current = true;
          pendingSkillPickerRef.current = typedUseSkill;
          if (typedFiles) pendingFilesClientIdRef.current = latestClientIdBeforeCursor(ed);
          window.setTimeout(() => {
            try {
              if (!ed.isDestroyed) {
                if (typedTemplate && /(?:^|\s)\/template$/i.test(ed.getText())) ed.chain().focus().insertContent(" /").run();
                if (typedSkill && /(?:^|\s)\/skill$/i.test(ed.getText())) ed.chain().focus().insertContent(" /").run();
                if (typedUseSkill && /(?:^|\s)\/use skill$/i.test(ed.getText())) ed.chain().focus().insertContent(" ~").run();
                if (typedFiles && /(?:^|\s)\/files$/i.test(ed.getText())) ed.chain().focus().insertContent(" #").run();
              }
            } finally {
              autoHierarchyTriggerRef.current = false;
            }
          }, 0);
        }
      },
    });

    // `useEditor`'s `editable` option only applies at creation — sync later
    // disabled-prop changes (e.g. while a response is streaming) explicitly.
    useEffect(() => {
      editor?.setEditable(!disabled);
    }, [editor, disabled]);

    useImperativeHandle(ref, () => ({
      getText: () => {
        if (!editor) return "";
        clearAutoCommandTrigger(editor);
        return editor.getText();
      },
      getMentionedClients: () =>
        extractMentionNodes(editor?.getJSON(), "mention")
          .filter((item) => !item.id.startsWith("shared-file:") && !item.id.startsWith("private-file:")),
      getMentionedFiles: () =>
        extractMentionNodes(editor?.getJSON(), "fileMention")
          .filter((item) => item.id.startsWith("shared-file:") || item.id.startsWith("private-file:"))
          .map((item) => {
            const type = item.id.startsWith("shared-file:") ? "shared-file" as const : "private-file" as const;
            return { ...item, id: item.id.slice(`${type}:`.length), type };
          }),
      getMentionedTemplates: () =>
        extractMentionNodes(editor?.getJSON(), "templateMention").filter((item) => !item.id.startsWith("skill:")),
      getMentionedSkills: () => {
        const selected = [
          ...extractMentionNodes(editor?.getJSON(), "ruleCommand"),
          ...extractMentionNodes(editor?.getJSON(), "templateMention"),
        ].filter((item) => item.id.startsWith("skill:"));
        return selected.map((item) => ({ ...item, id: item.id.slice("skill:".length) }));
      },
      getMentionedRules: () => extractMentionNodes(editor?.getJSON(), "ruleMention"),
      setText: (text: string) => { editor?.commands.setContent(text); },
      clear: () => { editor?.commands.clearContent(); },
      focus: () => { editor?.commands.focus(); },
    }), [editor]);

    return (
      <Box
        sx={{
          flex: 1,
          position: "relative",
          display: "flex",
          alignItems: "center",
          // Matches the mic/send IconButtons' 32px height — the parent row uses
          // align-items: flex-end (so the row grows upward as you type multiple
          // lines, keeping the icons pinned to the bottom), which otherwise left a
          // single line of text sitting low against the bottom of a taller row.
          minHeight: 32,
          fontSize: "0.95rem",
          "& .ProseMirror": {
            outline: "none",
            width: "100%",
            maxHeight: "5.6rem",
            overflowY: "auto",
          },
          "& .ProseMirror p": { margin: 0 },
          "& .chat-mention": {
            color: "primary.main",
            backgroundColor: "rgba(25, 118, 210, 0.10)",
            border: "1px solid rgba(25, 118, 210, 0.28)",
            borderRadius: "6px",
            fontWeight: 600,
            padding: "1px 5px",
            whiteSpace: "nowrap",
          },
          "& .chat-template-mention": {
            color: "secondary.main",
            fontWeight: 500,
          },
          "& .chat-rule-mention": {
            color: "warning.dark",
            fontWeight: 500,
          },
          "& .chat-file-mention": {
            color: "#00695c",
            backgroundColor: "rgba(0, 105, 92, 0.10)",
            border: "1px solid rgba(0, 105, 92, 0.28)",
            borderRadius: "6px",
            fontWeight: 600,
          },
          "& .chat-auto-command-trigger": {
            display: "inline-block",
            width: 0,
            overflow: "hidden",
            opacity: 0,
          },
        }}
        onClick={() => editor?.commands.focus()}
      >
        {isEmpty && (
          <Typography
            sx={{
              position: "absolute",
              top: "50%",
              left: 0,
              transform: "translateY(-50%)",
              fontSize: "0.95rem",
              color: "text.disabled",
              pointerEvents: "none",
            }}
          >
            {placeholder}
          </Typography>
        )}
        <EditorContent editor={editor} style={{ width: "100%" }} />
      </Box>
    );
  },
);

ChatMessageInput.displayName = "ChatMessageInput";
export default ChatMessageInput;
